import { ColDef, ColGroupDef, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { StoneXGrid } from '../../../StoneX';
import { StoneXNumber } from '../../../StoneXMui';
import { StorageTable, StorageTableRow } from './EiaGasStorageLogic';

export interface EiaGasStorageTableProps {
  table: StorageTable;
  showFiveYear: boolean;
}

const WEEK_WIDTH = 80;
const WEEK_ENDING_WIDTH = 120;
const VOLUME_WIDTH = 130;
const GRID_BORDER_WIDTH = 2;

function formatWeekEnding(params: ValueFormatterParams<StorageTableRow, Date | undefined>): string {
  return params.value ? dayjs(params.value).format('MM/DD/YYYY') : '';
}

function renderVolume(params: ICellRendererParams<StorageTableRow, number | null | undefined>) {
  return <StoneXNumber number={params.value ?? undefined} ifNoNumber="" />;
}

function volumeColumn(headerName: string, colId: string, getValue: (row: StorageTableRow) => number | null | undefined): ColDef<StorageTableRow> {
  return {
    headerName,
    colId,
    headerClass: 'ag-centered-header',
    wrapHeaderText: true,
    autoHeaderHeight: true,
    valueGetter: (params: ValueGetterParams<StorageTableRow>) => (params.data ? getValue(params.data) : undefined),
    cellRenderer: renderVolume,
    width: VOLUME_WIDTH,
    sortable: false,
  };
}

/**
 * Weekly injections or withdrawals and total stocks through the calendar year of the most recent
 * selected season, beside the year before interpolated to the same dates, the five year average
 * and the other selected seasons' years.
 */
export default function EiaGasStorageTable(props: EiaGasStorageTableProps) {
  const { table, showFiveYear } = props;
  const { year, otherYears, rows } = table;

  const columns = useMemo<(ColDef<StorageTableRow> | ColGroupDef<StorageTableRow>)[]>(() => {
    const currentLabel = `${year}`;
    const yearAgoLabel = `${year - 1} (Interpolated)`;
    const fiveYearLabel = '5 Year Avg (Interpolated)';

    const weekColumns: ColDef<StorageTableRow>[] = [
      { headerName: 'Week', field: 'weekNumber', pinned: 'left', width: WEEK_WIDTH, headerClass: 'ag-centered-header', cellStyle: { justifyContent: 'center' }, sortable: false },
      {
        headerName: 'Week Ending',
        field: 'weekEnding',
        pinned: 'left',
        width: WEEK_ENDING_WIDTH,
        headerClass: 'ag-centered-header',
        valueFormatter: formatWeekEnding,
        cellStyle: { justifyContent: 'center' },
        sortable: false,
      },
    ];

    const changeGroup: ColGroupDef<StorageTableRow> = {
      headerName: 'Injection / Withdrawal',
      children: [
        volumeColumn(currentLabel, 'currentNetChange', row => row.current?.netChange),
        volumeColumn(yearAgoLabel, 'yearAgoNetChange', row => row.yearAgo?.netChange),
        ...(showFiveYear ? [volumeColumn(fiveYearLabel, 'fiveYearNetChange', row => row.fiveYear?.netChange)] : []),
      ],
    };

    const stocksGroup: ColGroupDef<StorageTableRow> = {
      headerName: 'Total Stocks',
      children: [
        volumeColumn(currentLabel, 'currentVolume', row => row.current?.volume),
        volumeColumn(yearAgoLabel, 'yearAgoVolume', row => row.yearAgo?.volume),
        ...(showFiveYear ? [volumeColumn(fiveYearLabel, 'fiveYearAverage', row => row.fiveYear?.average)] : []),
        ...otherYears.map(otherYear => volumeColumn(`${otherYear}`, `${otherYear}-volume`, row => row.otherYears[otherYear])),
      ],
    };

    return [...weekColumns, changeGroup, stocksGroup];
  }, [year, otherYears, showFiveYear]);

  // The grid fills whatever width it is given, so size the box to the columns and let it scroll
  // sideways on a narrow screen instead of stretching empty rows across the page.
  const volumeColumnCount = (showFiveYear ? 3 : 2) * 2 + otherYears.length;
  const tableWidth = WEEK_WIDTH + WEEK_ENDING_WIDTH + VOLUME_WIDTH * volumeColumnCount + GRID_BORDER_WIDTH;

  return (
    <div style={{ width: tableWidth, maxWidth: '100%' }}>
      <StoneXGrid
        rows={rows}
        columns={columns as ColDef<StorageTableRow>[]}
        hideGridOptions
        getRowId={params => `${params.data.weekNumber}`}
        gridOptions={{ domLayout: 'autoHeight', suppressMovableColumns: true }}
      />
    </div>
  );
}
